import { useState } from 'react';
import { 
  Zap, 
  Lightbulb,
  BookOpen, 
  Trash2, 
  FileText,
  Upload,
  Layers,
  ShoppingCart,
  ChevronRight,
  Hash,
  MessageSquare,
  Send,
  Database,
  History
} from 'lucide-react';
import callsIcon from '@/assets/icons/calls-icon.png';
import sprintIcon from '@/assets/icons/sprint-icon.png';
import inviteIcon from '@/assets/icons/invite-icon.png';
import createIcon from '@/assets/icons/create-icon.png';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

export type EngageAction = 
  | 'call-solutions' 
  | 'call-knowledge' 
  | 'call-destruction'
  | 'team-sprint'
  | 'invite-colleague'
  | 'create-kb'
  | 'import-content'
  | 'create-channel'
  | 'compose'
  | 'consume'
  | 'give-feedback'
  | 'direct-message'
  | 'connect-kbs'
  | 'view-history'
  | 'view-threads';

interface GibletEngageMenuProps {
  onAction: (action: EngageAction) => void;
  connectedKBsCount?: number;
  maxKBs?: number;
}

export const GibletEngageMenu = ({ onAction, connectedKBsCount = 0, maxKBs = 50 }: GibletEngageMenuProps) => {
  const [mainOpen, setMainOpen] = useState(false);
  const [callsOpen, setCallsOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const { isCompositionEnabled, isConsumptionEnabled } = useFeatureFlags();

  const handleAction = (action: EngageAction) => {
    onAction(action);
    setCallsOpen(false);
    setCreateOpen(false);
    setMainOpen(false);
  };

  return (
    <TooltipProvider delayDuration={300}>
      <Popover open={mainOpen} onOpenChange={setMainOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 rounded-full bg-primary/10 hover:bg-primary/20 text-primary flex-shrink-0"
              >
                <Zap className="h-3.5 w-3.5" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>Start a workflow</TooltipContent>
        </Tooltip>
        <PopoverContent 
          className="w-64 p-2" 
          side="right" 
          align="center"
          sideOffset={12}
          avoidCollisions={true}
          collisionPadding={16}
          style={{ maxHeight: 'calc(100vh - 4rem)' }}
        >
          <div className="flex flex-col gap-0.5">
            {/* Connect & Navigate Section */}
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 h-8 text-sm"
              onClick={() => handleAction('connect-kbs')}
            >
              <Database className="h-4 w-4 text-violet-500" />
              <span>Connect KBs</span>
              <span className="ml-auto text-xs text-muted-foreground">
                {connectedKBsCount}/{maxKBs}
              </span>
            </Button>

            <Button
              variant="ghost"
              className="w-full justify-start gap-3 h-8 text-sm"
              onClick={() => handleAction('view-history')}
            >
              <History className="h-4 w-4 text-orange-500" />
              <span>View History</span>
            </Button>

            <Button
              variant="ghost"
              className="w-full justify-start gap-3 h-8 text-sm"
              onClick={() => handleAction('view-threads')}
            >
              <Layers className="h-4 w-4 text-cyan-500" />
              <span>Threads</span>
            </Button>

            <div className="border-t my-0.5" />
            {/* Calls Section */}
            <Popover open={callsOpen} onOpenChange={setCallsOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-between h-8 text-sm"
                >
                  <span className="flex items-center gap-3">
                    <img src={callsIcon} alt="Calls" className="h-5 w-5 rounded" />
                    <span>Calls</span>
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </Button>
              </PopoverTrigger>
              <PopoverContent side="right" align="start" className="w-48 p-2">
                <div className="flex flex-col gap-0.5">
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-3 h-8 text-sm"
                    onClick={() => handleAction('call-solutions')}
                  >
                    <Lightbulb className="h-4 w-4 text-amber-500" />
                    <span>Solutions</span>
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-3 h-8 text-sm"
                    onClick={() => handleAction('call-knowledge')}
                  >
                    <BookOpen className="h-4 w-4 text-blue-500" />
                    <span>Knowledge</span>
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-3 h-8 text-sm"
                    onClick={() => handleAction('call-destruction')}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                    <span>Destruction</span>
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            {/* Start Team Sprint */}
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 h-8 text-sm"
              onClick={() => handleAction('team-sprint')}
            >
              <img src={sprintIcon} alt="Team Sprint" className="h-5 w-5 rounded" />
              <span>Start Team Sprint</span>
            </Button>

            {/* Invite a Colleague */}
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 h-8 text-sm"
              onClick={() => handleAction('invite-colleague')}
            >
              <img src={inviteIcon} alt="Invite" className="h-5 w-5 rounded" />
              <span>Invite a Colleague</span>
            </Button>

            {/* Create Section */}
            <Popover open={createOpen} onOpenChange={setCreateOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-between h-8 text-sm"
                >
                  <span className="flex items-center gap-3">
                    <img src={createIcon} alt="Create" className="h-5 w-5 rounded" />
                    <span>Create</span>
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </Button>
              </PopoverTrigger>
              <PopoverContent side="right" align="start" className="w-48 p-2">
                <div className="flex flex-col gap-0.5">
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-3 h-8 text-sm"
                    onClick={() => handleAction('create-kb')}
                  >
                    <FileText className="h-4 w-4 text-indigo-500" />
                    <span>Knowledge Base</span>
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-3 h-8 text-sm"
                    onClick={() => handleAction('create-channel')}
                  >
                    <Hash className="h-4 w-4 text-teal-500" />
                    <span>Channel</span>
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-3 h-8 text-sm opacity-50 cursor-not-allowed"
                    disabled
                  >
                    <Layers className="h-4 w-4 text-muted-foreground" />
                    <span>Solution</span>
                    <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0">
                      Soon
                    </Badge>
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            <div className="border-t my-0.5" />

            {/* Communicate Section */}
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 h-8 text-sm"
              onClick={() => handleAction('give-feedback')}
            >
              <MessageSquare className="h-4 w-4 text-emerald-500" />
              <span>Give Feedback</span>
            </Button>

            <Button
              variant="ghost"
              className="w-full justify-start gap-3 h-8 text-sm"
              onClick={() => handleAction('direct-message')}
            >
              <Send className="h-4 w-4 text-sky-500" />
              <span>Direct Message</span>
            </Button>

            <div className="border-t my-0.5" />

            {/* Compose - Gated by bt2_composition_active */}
            <Button
              variant="ghost"
              className={`w-full justify-start gap-3 h-8 text-sm ${!isCompositionEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={!isCompositionEnabled}
              onClick={() => isCompositionEnabled && handleAction('compose')}
            >
              <Layers className={`h-4 w-4 ${isCompositionEnabled ? 'text-purple-500' : 'text-muted-foreground'}`} />
              <span>Compose</span>
              {!isCompositionEnabled && (
                <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0">
                  Soon
                </Badge>
              )}
            </Button>

            {/* Consume - Gated by bt3_consumption_active */}
            <Button
              variant="ghost"
              className={`w-full justify-start gap-3 h-8 text-sm ${!isConsumptionEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={!isConsumptionEnabled}
              onClick={() => isConsumptionEnabled && handleAction('consume')}
            >
              <ShoppingCart className={`h-4 w-4 ${isConsumptionEnabled ? 'text-green-500' : 'text-muted-foreground'}`} />
              <span>Consume</span>
              {!isConsumptionEnabled && (
                <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0">
                  Soon
                </Badge>
              )}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  );
};
